import { useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import { TiffinSevaDetailDialog } from "@project/admin/components/tiffin-seva-detail-dialog";
import { TiffinSevaFormFields } from "@project/admin/components/tiffin-seva-form-fields";
import type { Field } from "@project/admin/components/resource-table";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Eye, Pencil, CheckCircle2, XCircle } from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { tiffinSevaAdminService } from "@project/services/admin/tiffin-seva.service";

const tableFields: Field[] = [];

export default function TiffinSevaApplications() {
  const [viewTarget, setViewTarget] = useState<any | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // State for confirm dialog
  const [confirmAction, setConfirmAction] = useState<{
    type: "approve" | "reject";
    id: number;
    name: string;
  } | null>(null);

  const defaultValues = {
    tiffin_start_date: new Date().toISOString().split("T")[0],
    tiffin_end_date: "",
    name: "",
    address: "",
    age: "",
    gender: "Male",
    date_of_birth: "",
    mobile_number: "",
    categorys: "-",
    seva_category: "FREE",
    tiffin_type: "Daily",
    monthly_income: "",
    tiffin_recipient: "Self",
    reference_provider_name: "",
    reference_provider_mobile: "",
    reference_provider_address: "",
    reference_provider_occupation: "",
    tiffin_arrangement_details: "Lunch & Dinner Delivery",
    reason_for_providing_tiffin: "",
    identity_documents: ["Aadhaar Card"],
    fee_amount: "100",
    payment_status: "PAID",
    payment_mode: "CASH",
    payment_reference: "",
    family_members: [],
    step: "category",
  };

  const extraColumns = [
    {
      id: "created_at",
      label: "Date",
      render: (row: any) => {
        const dateStr = row.created_at || row.tiffin_start_date;
        if (!dateStr) return "—";
        try {
          return new Date(dateStr).toISOString().split("T")[0];
        } catch {
          return dateStr;
        }
      },
    },
    {
      id: "name",
      label: "Applicant Name",
      render: (row: any) => (
        <span className="font-bold text-foreground">{row.name}</span>
      ),
    },
    {
      id: "categorys",
      label: "Category",
      render: (row: any) => {
        const isSenior =
          row.categorys?.includes("Above") ||
          (row.age && Number(row.age) >= 60);
        return isSenior ? (
          <Badge className="bg-emerald-500/10 hover:bg-emerald-500/10 text-emerald-700 font-semibold border border-emerald-500/20 shadow-none rounded-full px-2.5 py-0.5">
            Above 60 (Senior)
          </Badge>
        ) : (
          <Badge className="bg-blue-500/10 hover:bg-blue-500/10 text-blue-700 font-semibold border border-blue-500/20 shadow-none rounded-full px-2.5 py-0.5">
            Below 60 (General)
          </Badge>
        );
      },
    },
    {
      id: "age",
      label: "Age",
      render: (row: any) => (row.age ? `${row.age} Yrs` : "—"),
    },
    {
      id: "mobile_number",
      label: "Mobile No",
      render: (row: any) => row.mobile_number || "—",
    },
    {
      id: "tiffin_start_date",
      label: "Start Date",
      render: (row: any) => {
        if (!row.tiffin_start_date) return "—";
        const parts = row.tiffin_start_date.split("-");
        if (parts.length === 3) {
          const [yyyy, mm, dd] = parts;
          return `${dd}-${mm}-${yyyy}`;
        }
        return row.tiffin_start_date;
      },
    },
    {
      id: "seva_category",
      label: "Seva Category",
      render: (row: any) => {
        const isFree =
          row.seva_category === "FREE" ||
          row.categorys?.includes("Above") ||
          (row.age && Number(row.age) >= 60);
        
        if (isFree) {
          return (
            <span className="text-emerald-700 font-semibold">
              Free (Trust Supported)
            </span>
          );
        }
        const typeStr = row.tiffin_type ? ` - ${row.tiffin_type}` : "";
        return (
          <span className="text-blue-700 font-semibold">Paid{typeStr}</span>
        );
      },
    },
    {
      id: "payment_status",
      label: "Status",
      render: (row: any) => {
        const status = (row.payment_status || "PENDING").toUpperCase();
        if (status === "PAID") {
          return (
            <Badge className="bg-emerald-500/10 hover:bg-emerald-500/10 text-emerald-700 border-emerald-500/20shadow-none font-bold rounded-full px-2.5 py-0.5">
              PAID
            </Badge>
          );
        }
        if (status === "NOT_REQUIRED" || status === "FREE") {
          return (
            <Badge className="bg-amber-500/10 hover:bg-amber-500/10 text-amber-700 border-amber-500/20 shadow-none font-bold rounded-full px-2.5 py-0.5">
              NOT_REQUIRED
            </Badge>
          );
        }
        return (
          <Badge className="bg-amber-500/10 hover:bg-amber-500/10 text-amber-700 border-amber-500/20 shadow-none font-bold rounded-full px-2.5 py-0.5">
            PENDING
          </Badge>
        );
      },
    },
    {
      id: "application_status",
      label: "Application Status",
      render: (row: any) => {
        const status = (row.application_status || "PENDING").toUpperCase();
        if (status === "APPROVED") {
          return (
            <Badge className="bg-emerald-100 hover:bg-emerald-100 text-emerald-800 border-transparent shadow-none font-bold rounded-full px-2.5 py-0.5">
              APPROVED
            </Badge>
          );
        }
        if (status === "REJECTED") {
          return (
            <Badge className="bg-rose-100 hover:bg-rose-100 text-rose-800 border-transparent shadow-none font-bold rounded-full px-2.5 py-0.5">
              REJECTED
            </Badge>
          );
        }
        return (
          <Badge className="bg-amber-100 hover:bg-amber-100 text-amber-800 border-transparent shadow-none font-bold rounded-full px-2.5 py-0.5">
            PENDING
          </Badge>
        );
      },
    },
  ];

  return (
    <AdminShell
      title="Tiffin Seva Inquiries & Applications"
      subtitle="Manage, view, and add offline tiffin seva registrations"
    >
      <ApiResourceTable<any>
        key={refreshKey}
        title="Tiffin Seva Applications"
        labelField="name"
        fields={tableFields}
        extraColumns={extraColumns}
        searchFields={[
          "name",
          "mobile_number",
          "categorys",
          "seva_category",
          "payment_status",
        ]}
        defaultValues={defaultValues}
        enableCsvExport
        csvFileName="tiffin-seva-applications"
        dialogClassName="max-w-3xl"
        addButtonLabel="Add Offline Inquiry"
        list={(query) => tiffinSevaAdminService.listInquiriesPage(query)}
        loadRecordForEdit={(id) => tiffinSevaAdminService.getInquiry(id)}
        create={(payload) => {
          const formData = new FormData();
          Object.entries(payload).forEach(([key, val]) => {
            if (val !== undefined && val !== null) {
              if (key === "family_members") {
                formData.append(key, JSON.stringify(val));
              } else if (key === "identity_documents") {
                formData.append(key, JSON.stringify(val));
              } else if (key === "identity_document_file" && val instanceof File) {
                formData.append("identity_document", val);
              } else {
                formData.append(key, String(val));
              }
            }
          });
          return tiffinSevaAdminService.createInquiry(formData);
        }}
        update={async (id, payload) => {
          if (payload.identity_document_file instanceof File) {
            const formData = new FormData();
            Object.entries(payload).forEach(([key, val]) => {
              if (val !== undefined && val !== null) {
                if (key === "family_members") {
                  formData.append(key, JSON.stringify(val));
                } else if (key === "identity_documents") {
                  formData.append(key, JSON.stringify(val));
                } else if (key === "identity_document_file" && val instanceof File) {
                  formData.append("identity_document", val);
                } else {
                  formData.append(key, String(val));
                }
              }
            });
            const response = await tiffinSevaAdminService.updateInquiry(id, payload);
            return response;
          }
          return tiffinSevaAdminService.updateInquiry(id, payload);
        }}
        remove={(id) => tiffinSevaAdminService.deleteInquiry(id)}
        onView={(row) => setViewTarget(row)}
        renderRowActions={(row) => {
          const isSenior =
            row.categorys?.includes("Above") ||
            row.categorys === "Above 60 Years" ||
            (row.age && Number(row.age) >= 60);
          const appStatus = (row.application_status || "PENDING").toUpperCase();
          const showActions =
            (appStatus === "NEW" || appStatus === "PENDING") &&
            isSenior;
          return (
            <div className="flex items-center gap-1">
              {showActions && (
                <>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 rounded-full text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                    title="Approve"
                    onClick={() =>
                      setConfirmAction({
                        type: "approve",
                        id: row.id,
                        name: row.name,
                      })
                    }
                  >
                    <CheckCircle2 className="h-5 w-5" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 rounded-full text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    title="Reject"
                    onClick={() =>
                      setConfirmAction({
                        type: "reject",
                        id: row.id,
                        name: row.name,
                      })
                    }
                  >
                    <XCircle className="h-5 w-5" />
                  </Button>
                  <div className="h-5 w-[1px] bg-border mx-2" />
                </>
              )}
            </div>
          );
        }}
        renderForm={({ editing, setEditing }) => (
          <TiffinSevaFormFields
            editing={editing as Record<string, any> | null}
            setEditing={setEditing}
          />
        )}
        buildPayload={(editing) => {
          if (!editing) {
            return null;
          }
          if (!editing.tiffin_start_date) {
            projectToast.error("Start date is required.");
            return null;
          }
          if (!editing.name) {
            projectToast.error("Applicant name is required.");
            return null;
          }
          if (!editing.address) {
            projectToast.error("Address is required.");
            return null;
          }
          if (!editing.age) {
            projectToast.error("Age is required.");
            return null;
          }
          if (!editing.gender) {
            projectToast.error("Gender is required.");
            return null;
          }
          if (!editing.date_of_birth) {
            projectToast.error("Date of birth is required.");
            return null;
          }
          if (!editing.mobile_number) {
            projectToast.error("Mobile number is required.");
            return null;
          }
          if (!editing.reason_for_providing_tiffin) {
            projectToast.error("Reason is required.");
            return null;
          }

          return editing as Record<string, unknown>;
        }}
        mapRecordToForm={(record) => {
          return {
            ...record,
            family_members: typeof record.family_members === "string"
              ? JSON.parse(record.family_members)
              : record.family_members || [],
            identity_documents: typeof record.identity_documents === "string"
              ? JSON.parse(record.identity_documents)
              : record.identity_documents || [],
          };
        }}
      />

      <TiffinSevaDetailDialog
        open={!!viewTarget}
        onOpenChange={(open) => {
          if (!open) {
            setViewTarget(null);
          }
        }}
        inquiryId={viewTarget?.id ?? null}
        preview={viewTarget}
      />

      {/* Approve/Reject Confirmation Modal */}
      <Dialog
        open={!!confirmAction}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <DialogContent className="max-w-md p-7 rounded-3xl bg-background border border-border">
          <DialogHeader className="space-y-3">
            <div className="flex items-start gap-4">
              {confirmAction?.type === "approve" ? (
                <>
                  <CheckCircle2 className="h-10 w-10 text-emerald-600 shrink-0 mt-1" />
                  <DialogTitle className="text-4xl font-extrabold text-emerald-600 leading-tight tracking-tight">
                    Approve<br />Application
                  </DialogTitle>
                </>
              ) : (
                <>
                  <XCircle className="h-10 w-10 text-rose-600 shrink-0 mt-1" />
                  <DialogTitle className="text-4xl font-extrabold text-rose-600 leading-tight tracking-tight">
                    Reject<br />Application
                  </DialogTitle>
                </>
              )}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed pt-2">
              Are you sure you want to <strong>{confirmAction?.type}</strong> the
              tiffin seva application for <strong>{confirmAction?.name}</strong>?
            </p>
          </DialogHeader>
          <DialogFooter className="mt-6 flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmAction(null)}
              className="text-xs font-semibold px-4"
            >
              Cancel
            </Button>
            <Button
              type="button"
              className={
                confirmAction?.type === "approve"
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-6 py-2 h-9 rounded-xl"
                  : "bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-6 py-2 h-9 rounded-xl"
              }
              onClick={async () => {
                if (!confirmAction) return;
                try {
                  if (confirmAction.type === "approve") {
                    await tiffinSevaAdminService.approveInquiry(confirmAction.id);
                    projectToast.success("Application approved successfully.");
                  } else {
                    await tiffinSevaAdminService.rejectInquiry(confirmAction.id);
                    projectToast.success("Application rejected successfully.");
                  }
                  // Refresh table state
                  setRefreshKey((prev) => prev + 1);
                } catch (err) {
                  projectToast.error(`Failed to ${confirmAction.type} application.`);
                } finally {
                  setConfirmAction(null);
                }
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
